import { useAuthStore } from '@/store/auth';
import { cognitoRefreshSession } from '@/lib/cognito';
import { env } from './env';

interface ApiOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
  authenticated?: boolean;
  /** Internal — set true on retry attempt to prevent infinite refresh loops. */
  _retried?: boolean;
}

export class ApiError extends Error {
  constructor(
    public readonly statusCode: number,
    public readonly code: string,
    message: string,
    public readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * On a 401, clear the stored session and bounce the user to /login with the
 * current path/search captured in `?from=` so login navigates them back after
 * authenticating. Idempotent if already on /login (avoids redirect loops).
 */
function handleUnauthorized(): void {
  useAuthStore.getState().clearSession();
  if (typeof window === 'undefined') return;
  const here = `${window.location.pathname}${window.location.search}`;
  if (window.location.pathname === '/login') return;
  const from = encodeURIComponent(here);
  window.location.assign(`/login?from=${from}`);
}

/**
 * If the stored idToken is within `bufferSeconds` of expiring, do a silent
 * refresh via the Cognito SDK before firing the request. Cheap (just decodes
 * a date) and saves a 401 → refresh → retry round-trip on the wire.
 */
async function maybeRefreshTokens(bufferSeconds = 60): Promise<void> {
  const { tokens, setSession, user } = useAuthStore.getState();
  if (!tokens || !user) return;
  const remaining = tokens.expiresAt - Date.now();
  if (remaining > bufferSeconds * 1000) return;
  try {
    const fresh = await cognitoRefreshSession();
    if (fresh) setSession(fresh, user);
  } catch {
    // Refresh failed — let the next call get a 401 and trigger redirect.
  }
}

export async function apiFetch<T>(path: string, opts: ApiOptions = {}): Promise<T> {
  const url = `${env.apiUrl}${path.startsWith('/') ? path : `/${path}`}`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(opts.headers ?? {}),
  };

  if (opts.authenticated !== false) {
    await maybeRefreshTokens();
    const tokens = useAuthStore.getState().tokens;
    if (tokens?.idToken) headers['Authorization'] = `Bearer ${tokens.idToken}`;
  }

  const r = await fetch(url, {
    method: opts.method ?? 'GET',
    headers,
    ...(opts.body !== undefined ? { body: JSON.stringify(opts.body) } : {}),
  });

  // Auth-required calls that come back 401: try a one-shot silent refresh + retry,
  // and if that fails, redirect to /login.
  if (r.status === 401 && opts.authenticated !== false && !opts._retried) {
    try {
      const fresh = await cognitoRefreshSession();
      if (fresh) {
        const { user, setSession } = useAuthStore.getState();
        if (user) setSession(fresh, user);
        return apiFetch<T>(path, { ...opts, _retried: true });
      }
    } catch {
      // fallthrough to redirect
    }
    handleUnauthorized();
    throw new ApiError(401, 'UNAUTHORIZED', 'Sessão expirada — faça login novamente');
  }

  if (r.status === 204) return undefined as T;

  const text = await r.text();
  let parsed: { data?: unknown; error?: { code: string; message: string; details?: Record<string, unknown> } } = {};
  try {
    parsed = text ? JSON.parse(text) : {};
  } catch {
    if (!r.ok) throw new ApiError(r.status, 'INVALID_RESPONSE', text || 'Erro do servidor');
    return undefined as T;
  }

  // 403 from Cognito authorizer also indicates broken/missing token in some cases —
  // treat the very specific "missing token" message as auth failure too.
  if (r.status === 403 && opts.authenticated !== false && /missing.*token|invalid.*token/i.test(parsed.error?.message ?? '')) {
    handleUnauthorized();
    throw new ApiError(403, 'UNAUTHORIZED', 'Sessão expirada — faça login novamente');
  }

  if (!r.ok) {
    const e = parsed.error ?? { code: 'UNKNOWN', message: 'Erro do servidor' };
    throw new ApiError(r.status, e.code, e.message, e.details);
  }

  return parsed.data as T;
}

export const api = {
  get: <T>(path: string, opts?: Omit<ApiOptions, 'method' | 'body'>) =>
    apiFetch<T>(path, { ...opts, method: 'GET' }),
  post: <T>(path: string, body?: unknown, opts?: Omit<ApiOptions, 'method' | 'body'>) =>
    apiFetch<T>(path, { ...opts, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, opts?: Omit<ApiOptions, 'method' | 'body'>) =>
    apiFetch<T>(path, { ...opts, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, opts?: Omit<ApiOptions, 'method' | 'body'>) =>
    apiFetch<T>(path, { ...opts, method: 'PUT', body }),
  delete: <T>(path: string, opts?: Omit<ApiOptions, 'method' | 'body'>) =>
    apiFetch<T>(path, { ...opts, method: 'DELETE' }),
};
